// Tipos para o sistema de solicitações UrbNet

export type TipoSolicitacao = 'denuncia' | 'reclamacao' | 'sugestao' | 'elogio' | 'informacao';
export type StatusSolicitacao =
  | 'pendente'
  | 'EmAnalise'
  | 'agendada'
  | 'resolvida'
  | 'aprovada'
  | 'reprovada';
export type PrioridadeSolicitacao = 'critica' | 'alta' | 'media' | 'baixa';
export type TipoValidacao = 'Agente' | 'IA' ;
export type ResultadoValidacao = 'aprovada' | 'reprovada' | 'aguardando';
export type StatusSLA = 'dentro_prazo' | 'alerta' | 'estourado';

export interface CategoriaSolicitacao {
  id: string;
  nome: string;
  descricao?: string;
  sla_horas: number;
  ativo: boolean;
  municipio_id?: string;
  etiquetas?: string[];
}

export interface SolicitacaoAnexo {
  id: string;
  solicitacao_id: string;
  tipo: 'imagem' | 'documento' | 'video';
  url: string;
  nome_arquivo?: string;
  tamanho_bytes?: number;
  created_at: string;
}

export interface SolicitacaoHistorico {
  id: string;
  solicitacao_id: string;
  status_anterior?: StatusSolicitacao;
  status_novo: StatusSolicitacao;
  agente_id?: string;
  agente_nome?: string;
  observacao?: string;
  created_at: string;
}

export interface SolicitacaoAgente {
  id: string;
  solicitacao_id: string;
  agente_id: string;
  principal: boolean;
  created_at: string;
  // Dados do perfil do agente (join)
  profile?: {
    nome: string;
    email: string;
    cargo?: string;
  };
}

export interface SolicitacaoComentario {
  id: string;
  solicitacao_id: string;
  autor_id: string;
  autor_nome: string;
  conteudo: string;
  visivel_cidadao: boolean;
  created_at: string;
}

export interface Solicitacao {
  id: string;
  protocolo: string;
  municipio_id: string;
  ouvidoria_id?: string;
  
  // Identificação
  tipo: TipoSolicitacao;
  categoria_id?: string;
  categoria_texto?: string;
  categoria?: CategoriaSolicitacao;
  
  // Conteúdo
  titulo: string;
  descricao: string;
  localizacao_endereco?: string;
  localizacao_bairro?: string;
  localizacao_lat?: number;
  localizacao_lng?: number;
  data_ocorrencia?: string;
  
  // Validação
  tipo_validacao: TipoValidacao;
  resultado_validacao: ResultadoValidacao;
  justificativa_reprovacao?: string;
  grau_confianca_validacao?: number;
  
  // Status
  status: StatusSolicitacao;
  prioridade: PrioridadeSolicitacao;
  estadoAprovacao?: string;
  
  // Cidadão
  cidadao_id?: string;
  cidadao_nome?: string;
  cidadao_email?: string;
  cidadao_telefone?: string;
  cidadao_anonimo: boolean;
  
  // SLA
  sla_horas: number;
  sla_vencimento?: string;
  
  // Métricas
  apoios: number;
  visualizacoes: number;
  
  // Timestamps
  created_at: string;
  updated_at: string;
  data_primeira_resposta?: string;
  data_conclusao?: string;
  
  // Observações internas
  observacoes_internas?: string;
  
  // Relacionamentos (preenchidos via joins)
  anexos?: SolicitacaoAnexo[];
  historico?: SolicitacaoHistorico[];
  agentes?: SolicitacaoAgente[];
  comentarios?: SolicitacaoComentario[];
  agenteResponsavel?: {
    id: string;
    nome: string;
    email?: string;
    status?: string;
    fotoPerfil?: {
      id: string;
      arquivo: string;
    };
    arquivado?: boolean;
  };
}

export type EstadoAprovacaoSolicitacao = 'AguardandoAprovacao' | 'Aprovada' | 'Reprovada';

// Labels para estado de aprovação (filtro lateral)
export const ESTADO_APROVACAO_LABELS: Record<EstadoAprovacaoSolicitacao, string> = {
  AguardandoAprovacao: 'Aguardando aprovação',
  Aprovada: 'Aprovada',
  Reprovada: 'Reprovada',
};

// Status filtráveis para o select principal (sem aprovada/reprovada/arquivada)
export type StatusSolicitacaoFiltro = 'Pendente' | 'EmAnalise' | 'Agendada' | 'Resolvida' | 'AtribuidasAMim';

export const STATUS_FILTRO_LABELS: Record<StatusSolicitacaoFiltro, string> = {
  Pendente: 'Pendente',
  EmAnalise: 'Em análise',
  Agendada: 'Agendada',
  Resolvida: 'Resolvida',
  AtribuidasAMim: 'Atribuídas a mim',
};

export interface SolicitacaoFiltros {
  status?: StatusSolicitacao | 'all';
  tipo?: TipoSolicitacao | 'all';
  categoria_id?: string | 'all';
  prioridade?: PrioridadeSolicitacao | 'all';
  resultado_validacao?: ResultadoValidacao | 'all';
  estadoAprovacao?: EstadoAprovacaoSolicitacao | 'all';
  busca?: string;
  agente_id?: string;
  sla_status?: StatusSLA | 'all';
  // Hierarquia: supervisora inclui solicitações das supervisionadas (hierarquia-ouvidorias.md)
  incluirSupervisionadas?: boolean;
  pagina?: number;
  limite?: number;
}

export interface DashboardMetricas {
  total_abertas: number;
  total_em_analise: number;
  total_concluidas: number;
  total_arquivadas: number;
  total_reprovadas: number;
  aguardando_aprovacao: number;
  sla_dentro_prazo: number;
  sla_alerta: number;
  sla_estourado: number;
  sla_medio_percentual: number;
  tempo_medio_resposta_horas: number;
  tempo_medio_resolucao_horas: number;
  comparacao_mes_anterior: {
    total: number;
    percentual: number;
  };
}

// Labels para exibição
export const TIPO_LABELS: Record<TipoSolicitacao, string> = {
  denuncia: 'Denúncia',
  reclamacao: 'Reclamação',
  sugestao: 'Sugestão',
  elogio: 'Elogio',
  informacao: 'Pedido de Informação',
};

export const STATUS_LABELS: Record<StatusSolicitacao, string> = {
  pendente: 'Pendente',
  EmAnalise: 'Em análise',
  agendada: 'Agendada',
  resolvida: 'Resolvida',
  aprovada: 'Aprovada',
  reprovada: 'Reprovada',
};

export const PRIORIDADE_LABELS: Record<PrioridadeSolicitacao, string> = {
  critica: 'Crítica',
  alta: 'Alta',
  media: 'Média',
  baixa: 'Baixa',
};

export const VALIDACAO_LABELS: Record<TipoValidacao, string> = {
  Agente: 'Agente',
  IA: 'IA',
};

export const RESULTADO_VALIDACAO_LABELS: Record<ResultadoValidacao, string> = {
  aprovada: 'Aprovada',
  reprovada: 'Reprovada',
  aguardando: 'Aguardando',
};

export const SLA_STATUS_LABELS: Record<StatusSLA, string> = {
  dentro_prazo: 'Dentro do Prazo',
  alerta: 'Em Alerta',
  estourado: 'Estourado',
};

// Cores para badges
export const STATUS_COLORS: Record<StatusSolicitacao, string> = {
  // Pendente -> mesma paleta do SLA estourado
  pendente: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  // Em Análise -> laranja mais intensa
  EmAnalise: 'bg-orange-200 text-orange-900 dark:bg-orange-900/50 dark:text-orange-200',
  // Agendada -> verde-amarelado com leve transparência
  agendada: 'bg-[#CEDC38]/60 text-[#303312] dark:bg-[#cedc38]/40 dark:text-[#f7fbe1]',
  // Resolvida -> mesma paleta da prioridade Baixa
  resolvida: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  aprovada: 'bg-blue-200 text-blue-900 dark:bg-blue-900/50 dark:text-blue-100',
  reprovada: 'bg-red-200 text-red-900 dark:bg-red-900/50 dark:text-red-100',
};

export const PRIORIDADE_COLORS: Record<PrioridadeSolicitacao, string> = {
  critica: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  alta: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  media: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  baixa: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
};

export const SLA_STATUS_COLORS: Record<StatusSLA, string> = {
  dentro_prazo: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  alerta: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  estourado: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

// Funções utilitárias
export function calcularStatusSLA(slaVencimento?: string): StatusSLA {
  if (!slaVencimento) return 'dentro_prazo';
  
  const vencimento = new Date(slaVencimento);
  const agora = new Date();
  const horasRestantes = (vencimento.getTime() - agora.getTime()) / (1000 * 60 * 60);
  
  if (horasRestantes < 0) return 'estourado';
  if (horasRestantes <= 12) return 'alerta';
  return 'dentro_prazo';
}

export function calcularTempoDecorrido(dataInicio: string): string {
  const inicio = new Date(dataInicio);
  const agora = new Date();
  const diff = agora.getTime() - inicio.getTime();
  
  const horas = Math.floor(diff / (1000 * 60 * 60));
  const dias = Math.floor(horas / 24);
  
  if (dias > 0) {
    return `${dias}d ${horas % 24}h`;
  }
  return `${horas}h`;
}

export function calcularTempoRestante(slaVencimento?: string): string {
  if (!slaVencimento) return '-';
  
  const vencimento = new Date(slaVencimento);
  const agora = new Date();
  const diff = vencimento.getTime() - agora.getTime();
  
  if (diff < 0) {
    const horasAtrasadas = Math.abs(Math.floor(diff / (1000 * 60 * 60)));
    const dias = Math.floor(horasAtrasadas / 24);
    if (dias > 0) {
      return `-${dias}d ${horasAtrasadas % 24}h`;
    }
    return `-${horasAtrasadas}h`;
  }
  
  const horas = Math.floor(diff / (1000 * 60 * 60));
  const dias = Math.floor(horas / 24);
  
  if (dias > 0) {
    return `${dias}d ${horas % 24}h`;
  }
  return `${horas}h`;
}
